import { useTranslation } from "react-i18next";
import { useMemo } from "react";
import { Box } from "@caspeco/casper-ui-library.components.box";
import { Button } from "@caspeco/casper-ui-library.components.button";
import { Text } from "@caspeco/casper-ui-library.components.text";
import { ThemeSpaceVariable } from "@caspeco/casper-ui-library.base-ui.theme";
import { ModalFooter } from "@caspeco/casper-ui-library.components.modal";
import { Alert, AlertType } from "@caspeco/casper-ui-library.components.alert";
import { Icons } from "@caspeco/casper-ui-library.components.icon";
import { QuantityButton } from "@caspeco/casper-ui-library.components.quantity-button";
import { PriceIncludingVat } from "types/price";
import Price from "components/price/Price";

interface IArticleModalFooter {
	totalPrice: PriceIncludingVat;
	/** Number of article to add to/remove from cart */
	quantity: number;
	/** Is currently adding/removing item from cart */
	isSubmitting?: boolean;
	/** Cannot add article to cart or increase amount. */
	isNotAvailableForPurchase: boolean;
	/** Article modal has been opened from Cart (article exists in cart) */
	isOpenedFromCart?: boolean;
	/** Minimum allowed amount of Article. */
	minQuantity: number;
	onChange: (quantity: number) => void;
	/** Info text to be displayed in relation to purchasing (or not be able to be purchasing)
	 * article at current time. */
	alert?: { description: string; type: AlertType; icon?: Icons };
	onUpdate: () => void;
	onDelete: () => void;
	onAdd: () => void;
	hasChanges: boolean;
}

export function ArticleModalFooter({
	totalPrice,
	quantity,
	minQuantity,
	isOpenedFromCart,
	isSubmitting,
	isNotAvailableForPurchase,
	onChange,
	alert,
	onDelete,
	onAdd,
	onUpdate,
	hasChanges,
}: IArticleModalFooter) {
	const { t } = useTranslation();

	let submitAction: "delete" | "update" | "add";
	if (quantity === 0) {
		// At quantity 0 the submit action should be removing the article
		submitAction = "delete";
	} else if (isOpenedFromCart && quantity > 0) {
		// Submit is update for existing (in Cart) article
		submitAction = "update";
	} else {
		submitAction = "add";
	}

	const onChangeQuantity = (type: "increase" | "decrease") => {
		let newQuantity = quantity;
		return () => {
			switch (type) {
				case "increase":
					newQuantity += 1;
					break;
				case "decrease":
					newQuantity -= 1;
					break;
				default:
					break;
			}
			onChange(newQuantity);
		};
	};

	const submitButtonStatus: { submitButtonText: string; showPrice: boolean } = useMemo(() => {
		switch (submitAction) {
			case "delete":
				return { submitButtonText: t("action_remove"), showPrice: false };
			case "update":
				if (hasChanges) {
					return { submitButtonText: t("action_update"), showPrice: true };
				}
				return { submitButtonText: t("misc_done"), showPrice: false };
			case "add":
				return { submitButtonText: t("action_add"), showPrice: true };
			default:
				return { submitButtonText: t("action_add"), showPrice: true };
		}
	}, [submitAction, t, hasChanges]);

	const { submitButtonText, showPrice } = submitButtonStatus;

	const onSubmit = () => {
		switch (submitAction) {
			case "delete":
				onDelete();
				break;
			case "update":
				onUpdate();
				break;
			case "add":
			default:
				onAdd();
				break;
		}
	};

	const renderActionButtons = (): JSX.Element | null => {
		if (isNotAvailableForPurchase) return null;
		return (
			<Box w="100%" display="flex" alignItems="center" gap={ThemeSpaceVariable.Medium}>
				<Box aria-label={t("action_change")}>
					<QuantityButton
						onClickMinus={onChangeQuantity("decrease")}
						minusButtonAriaLabel={t("action_decrease_quantity")}
						min={minQuantity}
						disabled={isSubmitting}
						quantity={quantity}
						onClickPlus={onChangeQuantity("increase")}
						plusButtonAriaLabel={t("action_increase_quantity")}
						max={isNotAvailableForPurchase ? 0 : 99}
					/>
				</Box>
				<Box display="flex" alignContent="center" w="100%">
					<Button
						isLoading={isSubmitting}
						size="lg"
						onClick={onSubmit}
						variant="primary"
						w="100%"
						display="flex"
						alignContent="center"
						justifyContent="center"
						gap={ThemeSpaceVariable.XSmall}
					>
						<Text fontWeight="inherit" fontSize="inherit" fontFamily="inherit">
							{submitButtonText}
						</Text>
						{showPrice && <Price inHousePrice={totalPrice} hideIfZero={false} />}
					</Button>
				</Box>
			</Box>
		);
	};

	return (
		<ModalFooter
			pt={ThemeSpaceVariable.Medium}
			px={ThemeSpaceVariable.Medium}
			pb="calc(1em + env(safe-area-inset-bottom))"
			display="flex"
			flexDirection="column"
			zIndex={1}
			gap={ThemeSpaceVariable.Medium}
		>
			{alert && (
				<Alert
					pl={ThemeSpaceVariable.Medium}
					pr={ThemeSpaceVariable.Small}
					type={alert.type}
					icon={alert.icon}
					iconVerticalAlign="flex-start"
				>
					{alert.description}
				</Alert>
			)}
			{renderActionButtons()}
		</ModalFooter>
	);
}
